import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Clock, Mail, Phone, ArrowRight, RefreshCw, XCircle, FileText, Sparkles } from "lucide-react";
import { SEOHead } from "../components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, success, failed
  const [data, setData] = useState(null);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    pollPayment(sessionId, 0);
  }, [sessionId]);

  const pollPayment = async (sid, attempts) => {
    if (attempts > 15) { setStatus("failed"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      if (res.data.payment_status === "paid") {
        setData(res.data);
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("failed");
      } else {
        setTimeout(() => pollPayment(sid, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPayment(sid, attempts + 1), 2000);
    }
  };

  // Polling state
  if (status === "polling") {
    return (
      <main className="pt-16 min-h-screen bg-[#F4F4F5] flex items-center justify-center" data-testid="thank-you-polling">
        <SEOHead title="Thank You" description="Thank you for your purchase." path="/thank-you" noindex={true} />
        <div className="text-center px-4">
          <div className="w-20 h-20 bg-[#FFD600] border-3 border-black rounded-full flex items-center justify-center mx-auto mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            <RefreshCw size={36} className="animate-spin text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Confirming Your Payment...</h1>
          <p className="text-lg text-gray-600 max-w-md mx-auto">Hang tight — we're confirming your payment with Stripe. This usually takes just a few seconds.</p>
        </div>
      </main>
    );
  }

  // Failed state
  if (status === "failed") {
    return (
      <main className="pt-16 min-h-screen bg-[#F4F4F5] flex items-center justify-center" data-testid="thank-you-failed">
        <div className="text-center px-4 max-w-lg">
          <div className="w-20 h-20 bg-red-100 border-3 border-black rounded-full flex items-center justify-center mx-auto mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            <XCircle size={36} className="text-red-500" />
          </div>
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Payment Not Completed</h1>
          <p className="text-lg text-gray-600 mb-8">Your payment was cancelled or couldn't be confirmed. No charges were made.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/purchase" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="try-again-btn">
              Try Again <ArrowRight size={16} />
            </Link>
            <a href="tel:0466476264" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="call-us-btn">
              <Phone size={16} /> Call Us
            </a>
          </div>
        </div>
      </main>
    );
  }

  // Success state
  return (
    <main className="pt-16 min-h-screen bg-[#F4F4F5]" data-testid="thank-you-success">
      {/* Confetti-style header */}
      <div className="bg-[#FFD600] border-b-4 border-black py-16 text-center relative overflow-hidden">
        <div className="relative z-10">
          <div className="w-24 h-24 bg-green-500 border-4 border-black rounded-full flex items-center justify-center mx-auto mb-6" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}>
            <Check size={48} className="text-white" strokeWidth={3} />
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="thank-you-title">
            Thank You!
          </h1>
          <p className="text-lg md:text-xl font-bold max-w-xl mx-auto px-4">
            Your website order has been placed successfully. We're excited to get started!
          </p>
        </div>
      </div>

      <div className="container-main max-w-3xl py-12 px-4">
        {/* Ticket Number */}
        {data?.ticket_number && (
          <div className="bg-black text-white border-2 border-black rounded-2xl p-8 mb-8 text-center" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,0.3)" }} data-testid="ticket-section">
            <p className="text-sm font-bold uppercase tracking-widest text-[#FFD600] mb-2">Your Order Reference</p>
            <p className="text-5xl md:text-6xl font-black" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="ticket-number">{data.ticket_number}</p>
            <p className="text-sm text-gray-400 mt-3">Save this number for your records</p>
          </div>
        )}

        {/* What Happens Next */}
        <div className="bg-white border-2 border-black rounded-2xl p-8 mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h2 className="text-2xl font-black mb-6 flex items-center gap-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Sparkles size={24} className="text-[#FFD600]" /> What Happens Next
          </h2>
          <div className="space-y-5">
            {[
              { icon: Mail, title: "Check Your Inbox", desc: "A confirmation email has been sent to you with your order details." },
              { icon: Phone, title: "We'll Be In Touch Within 24 Hours", desc: "Our team will reach out to discuss your website goals and collect your content." },
              { icon: FileText, title: "Send Us Your Content", desc: "We'll need your logo, photos, and any text you'd like on the site. Don't stress — we'll guide you through it." },
              { icon: Clock, title: "Your Website Goes Live", desc: "Once we have everything, your website will be live within 48 hours. That's a promise." },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD600] border-2 border-black rounded-xl flex items-center justify-center shrink-0 font-black text-lg" style={{ boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}>
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-black text-base">{step.title}</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-8 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="text-xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Have Questions?</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0466476264" className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold px-6 py-3 rounded-xl border-2 border-black hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,0.3)" }} data-testid="contact-phone">
              <Phone size={18} /> 0466 476 264
            </a>
            <a href="mailto:hello@nofusswebsites.com.au" className="inline-flex items-center justify-center gap-2 bg-white text-black font-bold px-6 py-3 rounded-xl border-2 border-black hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,0.3)" }} data-testid="contact-email">
              <Mail size={18} /> hello@nofusswebsites.com.au
            </a>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-10">
          <Link to="/" className="btn-primary inline-flex items-center gap-2 text-lg" data-testid="back-home-btn">
            Back to Home <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </main>
  );
}
